import { ExpandCircleDown } from '@mui/icons-material';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  List,
  ListItem,
  Stack,
  styled,
  Typography,
} from '@mui/material';
import { DataGridPremium, GridColDef } from '@mui/x-data-grid-premium';
import { useCalculusTFunction } from '@retail/calculus/i18n';
import { IndexedUploadedCustomerMarkup } from '@retail/customer-markup/types';
import { getLLDate } from '@retail/utils';
import { dataGridWithCellBordersClass } from '@shared/styles';
import { useMemo } from 'react';

interface Props {
  missingColumns: string[];
  invalidDataRows: IndexedUploadedCustomerMarkup[];
  validDataRowsLength: number;
}

export function UploadCustomerMarkupValidation({
  missingColumns,
  invalidDataRows,
  validDataRowsLength,
}: Props) {
  const t = useCalculusTFunction();

  const errorColumns = invalidDataRows
    .map((row) => row.rowErrors)
    .flat()
    .map((error) => error.column + '');

  const errorRows: GridColDef<IndexedUploadedCustomerMarkup>[] = useMemo(
    () => [
      {
        field: 'index',
        headerAlign: 'left',
        align: 'left',
        width: 60,
        headerName: 'Index',
        renderCell: ({ row }) => row.rowIndex,
      },
      {
        field: 'groupNumber',
        width: 80,
        headerName: 'group-number',
        renderCell: ({ row }) => {
          const error = row.rowErrors.find((error) => error.column === 'groupNumber');
          return (
            <Stack>
              {row.values.groupNumber}
              {error && (
                <Typography color="error.light" variant="body3" whiteSpace="nowrap">
                  {error.reason === 'invalid' && t('calculus.error.invalidValue')}
                </Typography>
              )}
            </Stack>
          );
        },
      },
      {
        field: 'groupCode',
        width: 80,
        headerName: 'group-code',
        renderCell: ({ row }) => {
          const error = row.rowErrors.find((error) => error.column === 'groupCode');
          return (
            <Stack>
              {row.values.groupCode}
              {error && (
                <Typography color="error.light" variant="body3" whiteSpace="nowrap">
                  {error.reason === 'invalid' && t('calculus.error.invalidValue')}
                </Typography>
              )}
            </Stack>
          );
        },
      },
      {
        field: 'mgItemNumber',
        width: 90,
        headerName: 'mgItem- nr',
        renderCell: ({ row }) => {
          const error = row.rowErrors.find((error) => error.column === 'mgItemNumber');
          return (
            <Stack>
              {row.values.mgItemNumber}
              {error && (
                <Typography color="error.light" variant="body3" whiteSpace="nowrap">
                  {error.reason === 'invalid' && t('calculus.error.invalidValue')}
                </Typography>
              )}
            </Stack>
          );
        },
      },
      {
        field: 'gtinCode',
        width: 100,
        headerName: 'gtin-code',
        renderCell: ({ row }) => {
          const error = row.rowErrors.find((error) => error.column === 'gtinCode');
          return (
            <Stack>
              {row.values.gtinCode}
              {error && (
                <Typography color="error.light" variant="body3" whiteSpace="nowrap">
                  {error.reason === 'invalid' && t('calculus.error.invalidValue')}
                </Typography>
              )}
            </Stack>
          );
        },
      },
      {
        field: 'validFrom',
        width: 90,
        headerName: 'Valid-from',
        renderCell: ({ row }) =>
          row.values.validFrom ? getLLDate(row.values.validFrom) : row.values.validFrom,
      },
      {
        field: 'customerFactor',
        flex: 1,
        headerName: 'Customer-factor',
        renderCell: ({ row }) => {
          const error = row.rowErrors.find((error) => error.column === 'customerFactor');
          return (
            <Stack>
              {row.values.customerFactor}
              {error && (
                <Typography color="error.light" variant="body3">
                  {error.reason === 'invalid' && t('calculus.error.invalidValue')}
                  {error.reason === 'requiredMissing' && t('calculus.error.requiredField')}
                </Typography>
              )}
            </Stack>
          );
        },
      },
    ],
    [t]
  );

  const rowErrorColumns = useMemo(
    () => errorRows.filter((row) => errorColumns.includes(row.field) || row.field === 'index'),
    [errorColumns, errorRows]
  );

  return (
    <Stack gap={1}>
      {missingColumns.length > 0 ? (
        <Alert severity="error" sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography variant="subtitle1">
            {t('calculus.customerMarkup.missingColumns')}:
          </Typography>
          <List
            sx={{
              listStyleType: 'disc',
              listStylePosition: 'inside',
            }}
          >
            {missingColumns.map((column) => (
              <ListItem key={column} sx={{ py: 0, display: 'list-item' }}>
                {column}
              </ListItem>
            ))}
          </List>
        </Alert>
      ) : (
        <>
          {invalidDataRows.length > 0 && (
            <Alert severity="error" sx={{ display: 'flex', alignItems: 'center' }}>
              <Stack gap={1}>
                <Typography variant="subtitle1" flexGrow={1}>
                  {invalidDataRows.length === 1
                    ? t('calculus.customerMarkup.invalidRowSingular')
                    : t('calculus.customerMarkup.invalidRowPlural', {
                        count: invalidDataRows.length,
                      })}
                </Typography>

                <StyledAccordion>
                  <AccordionSummary expandIcon={<ExpandCircleDown />} id="mgItem">
                    <Typography variant="subtitle2" pr={24}>
                      {t('calculus.customerMarkup.invalidRowsDesc')}:
                    </Typography>
                  </AccordionSummary>

                  <AccordionDetails>
                    <DataGridPremium<IndexedUploadedCustomerMarkup>
                      columns={rowErrorColumns}
                      rows={invalidDataRows}
                      className={dataGridWithCellBordersClass}
                      getRowId={(row) => row.rowIndex}
                      hideFooter
                      disableColumnMenu
                      disableColumnReorder
                      disableColumnSorting
                    />
                  </AccordionDetails>
                </StyledAccordion>
              </Stack>
            </Alert>
          )}
          {validDataRowsLength > 0 && (
            <Alert severity="success">
              {t('calculus.customerMarkup.validRows', { count: validDataRowsLength })}
            </Alert>
          )}
        </>
      )}
    </Stack>
  );
}

const StyledAccordion = styled(Accordion)(({ theme }) => ({
  backgroundColor: 'inherit',
  border: `1px solid ${theme.vars.palette.error.main}`,
}));
